import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import {
  LayoutGrid,
  BarChart2,
  Settings,
  LogOut,
  Menu,
  PanelLeft,
  PanelRight,
  User as UserIcon
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import artieImg from "@/assets/images/artie.png";
import { cn } from "@/lib/utils";

export function Sidebar() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false); // Mobile sheet state
  // Initialize desktop sidebar state from local storage, default to false (collapsed)
  const [isExpanded, setIsExpanded] = useState(() => {
    const stored = localStorage.getItem("sidebar_expanded");
    return stored === "true";
  });
  const [isHoveringLogo, setIsHoveringLogo] = useState(false);

  const toggleSidebar = (value: boolean) => {
    setIsExpanded(value);
    localStorage.setItem("sidebar_expanded", String(value));
  };


  const getImageUrl = (keyOrUrl?: string | null) => {
    if (!keyOrUrl) return undefined;
    if (keyOrUrl.startsWith("blob:") || keyOrUrl.startsWith("http")) return keyOrUrl;
    const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/api\/v1\/?$/, '') || '';
    return `${baseUrl}/api/v1/integrations/upload/screenshot/view?key=${encodeURIComponent(keyOrUrl)}`;
  };

  const handleLogout = () => {
    logout();
    queryClient.removeQueries();
    navigate("/login");
    apiClient.logout().catch(err => console.error("Background logout error:", err));
  };

  const isActive = (path: string) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') return true;
    if (path !== '/dashboard' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const getNavItemClass = (path: string, isMobile = false) => {
    const active = isActive(path);
    const baseClass = "sidebar-item group relative flex items-center p-2.5 rounded-lg transition-all duration-300";
    
    // Always align start to prevent icon jumping
    const alignment = "justify-start";
    
    // Colors
    const colors = active
      ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
      : "text-muted-foreground hover:text-primary hover:bg-primary/5";

    return cn(baseClass, alignment, colors, isExpanded ? "w-full" : "");
  };

  const SidebarContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className="flex flex-col h-full justify-between py-6">
      <div className="flex flex-col gap-6 w-full">
        {/* Sidebar Header: Logo & Toggle */}
        <div className={cn("flex items-center min-h-[40px] relative transition-all duration-300 justify-between px-4")}>
          {/* Logo Area */}
          <div
            className="flex items-center gap-0 cursor-pointer overflow-hidden"
            onMouseEnter={() => setIsHoveringLogo(true)}
            onMouseLeave={() => setIsHoveringLogo(false)}
            onClick={() => !isExpanded && !isMobile && toggleSidebar(true)}
          >
            {/* Collapsed Logo (or Logo Icon in Expanded) */}
            <div className="relative w-10 h-10 shrink-0">
               {/* Show 'A' logo normally */}
               <div className={cn(
                 "absolute inset-0 w-10 h-10 flex items-center justify-center transition-all duration-300",
                 (!isMobile && !isExpanded && isHoveringLogo) ? "opacity-0 scale-90" : "opacity-100 scale-100"
               )}>
                 <img src={artieImg} alt="Artie" className="w-full h-full object-contain" />
               </div>

               {/* Show Toggle Icon on Hover (Collapsed only) */}
               {!isMobile && !isExpanded && (
                 <div className={cn(
                   "absolute inset-0 w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary transition-all duration-300",
                   isHoveringLogo ? "opacity-100 scale-100" : "opacity-0 scale-90"
                 )}>
                   <PanelLeft className="w-6 h-6" />
                 </div>
               )}
            </div>

            {/* Expanded Text Logo */}
            <span
              className={cn(
                "font-bold font-cabinet text-xl text-foreground whitespace-nowrap overflow-hidden transition-all duration-300",
                isExpanded || isMobile ? "max-w-[200px] opacity-100 ml-3" : "max-w-0 opacity-0 ml-0"
              )}
            >
              Artie
            </span>
          </div>

          {/* Toggle Button (Expanded Only) */}
          <div className={cn(
             "transition-all duration-300 overflow-hidden",
             isExpanded && !isMobile ? "w-10 opacity-100 scale-100" : "w-0 opacity-0 scale-0"
          )}>
             <Button
               variant="ghost"
               size="icon"
               className="h-10 w-10 text-muted-foreground hover:text-primary hover:bg-primary/5 [&_svg]:size-6"
               onClick={() => toggleSidebar(false)}
             >
               <PanelRight />
             </Button>
          </div>
        </div>

        <div className={cn("h-[1px] bg-border transition-all duration-300 ease-in-out",
          isExpanded || isMobile ? "mx-4" : "mx-2"
        )}></div>

        {/* Nav Items */}
        <div className="flex flex-col gap-2 w-full px-4">
          {/* Dashboard */}
          <Link
            to={user?.role === 'brand_user' ? "/brand-dashboard" : "/dashboard"}
            onClick={() => isMobile && setIsOpen(false)}
            className={getNavItemClass(user?.role === 'brand_user' ? '/brand-dashboard' : '/dashboard', isMobile)}
          >
            <LayoutGrid className="w-5 h-5 stroke-[2] shrink-0 transition-transform duration-300 group-hover:scale-110" />
            <span className={cn(
              "text-sm font-medium whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out",
              isExpanded || isMobile ? "max-w-[200px] opacity-100 ml-3" : "max-w-0 opacity-0 ml-0"
            )}>
              Dashboard
            </span>
            {!isExpanded && !isMobile && (
              <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                Dashboard
              </span>
            )}
          </Link>

          {/* Analytics - Only show for non-brand users */}
          {user?.role !== 'brand_user' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  disabled
                  className={cn(
                    "sidebar-item group relative flex items-center p-2.5 rounded-lg transition-all duration-300 justify-start",
                    "text-muted-foreground opacity-50 cursor-not-allowed",
                    isExpanded ? "w-full" : ""
                  )}
                >
                  <BarChart2 className="w-5 h-5 stroke-[2] shrink-0" />
                  <span className={cn(
                    "text-sm font-medium whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out",
                    isExpanded || isMobile ? "max-w-[200px] opacity-100 ml-3" : "max-w-0 opacity-0 ml-0"
                  )}>
                    Analytics
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Coming Soon</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {/* Settings - Only show for non-brand users */}
          {user?.role !== 'brand_user' && (
            <Link
              to="/profile"
              onClick={() => isMobile && setIsOpen(false)}
              className={getNavItemClass('/profile', isMobile)}
            >
              <Settings className="w-5 h-5 stroke-[2] shrink-0 transition-transform duration-300 group-hover:scale-110 rotate-0 group-hover:rotate-90" />
              <span className={cn(
                "text-sm font-medium whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out",
                isExpanded || isMobile ? "max-w-[200px] opacity-100 ml-3" : "max-w-0 opacity-0 ml-0"
              )}>
                Settings
              </span>
              {!isExpanded && !isMobile && (
                <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                  Settings
                </span>
              )}
            </Link>
          )}

          {/* Logout */}
          <Dialog>
            <DialogTrigger asChild>
              <button
                className={getNavItemClass('logout-action', isMobile)}
              >
                <LogOut className="w-5 h-5 stroke-[2] shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                <span className={cn(
                  "text-sm font-medium whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out",
                  isExpanded || isMobile ? "max-w-[200px] opacity-100 ml-3" : "max-w-0 opacity-0 ml-0"
                )}>
                  Log out
                </span>
                {!isExpanded && !isMobile && (
                  <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                    Log out
                  </span>
                )}
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Are you sure you want to log out?</DialogTitle>
                <DialogDescription>
                  You will be returned to the login screen.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button onClick={handleLogout}>Log out</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Bottom Actions: User Profile */}
      <div className="flex flex-col gap-4 w-full px-2">
        <div className={cn(
          "flex items-center w-full rounded-xl bg-secondary/50 bg-secondary shadow-sm transition-all duration-300 border border-transparent border-border outline-none cursor-pointer group justify-start overflow-hidden p-2.5"
        )}>
           <Avatar className="w-9 h-9 shrink-0 ring-2 ring-transparent transition-all group-hover:scale-105 group-hover:ring-primary/20">
              <AvatarImage
                src={getImageUrl(user?.profile_photo)}
                alt={user?.name || "User"}
                className="object-cover"
              />
              <AvatarFallback className="bg-muted text-muted-foreground">
                {user?.name?.charAt(0).toUpperCase() || "U"}
              </AvatarFallback>
           </Avatar>
           
           <div className={cn(
             "flex flex-col items-start text-left overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out",
             isExpanded || isMobile ? "max-w-[200px] opacity-100 ml-3" : "max-w-0 opacity-0 ml-0"
           )}>
             <span className="text-sm font-medium truncate w-full text-foreground/90">
               {user?.name || "User"}
             </span>
             <span className="text-xs text-muted-foreground truncate w-full">
               {user?.email || ""}
             </span>
           </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <nav
        className={cn(
          "hidden md:flex sticky top-0 h-screen z-50 flex-col bg-background/80 border-border border-r backdrop-blur-xl transition-all duration-300 ease-in-out",
          isExpanded ? "w-[256px]" : "w-[72px]"
        )}
        id="sidebar"
      >
        <SidebarContent />
      </nav>

      {/* Mobile Header with Hamburger */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-background/80 backdrop-blur-md border-b border-border z-50 px-4 flex items-center justify-between">
         <Link to="/" className="flex items-center justify-center w-8 h-8 rounded-lg bg-foreground text-background font-bold font-cabinet text-lg">
            A
         </Link>
         
         <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-6 w-6" />
                </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 border-r border-border">
                <SidebarContent isMobile={true} />
            </SheetContent>
         </Sheet>
      </div>
    </>
  );
}